import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { selectSupplementalItems } from '../../../redux/shared/selectors/applicationSelectors';
import { CONSTANTS } from '../../Helpers/Configuration';

/**
 * Filter out deleted files and detect re-upload scenario
 * Show upload button when:
 * 1. documentReviewStatus is 'clarificationNeeded' (admin requesting document/clarification), OR
 * 2. Files were previously uploaded and status went back to 'notReceived' (re-upload scenario after deletion)
 * @param {Array} files - Array of file entries
 * @param {string} documentReviewStatus - Current document review status
 * @returns {Object} - { activeFiles, hasDeletedFiles, showUploadButton, hadPreviousFiles }
 */
const filterActiveFiles = (files, documentReviewStatus) => {
    if (!Array.isArray(files)) {
        return { 
            activeFiles: [], 
            hasDeletedFiles: false,
            hadPreviousFiles: false,
            showUploadButton: false
        };
    }
    const activeFiles = files.filter(file => file?.status !== 'deleted');
    const hasDeletedFiles = files.some(file => file?.status === 'deleted');
    
    // Track if there were ever files in this group (including deleted ones)
    const hadPreviousFiles = files.length > 0;
    
    // Show upload button when:
    // 1. Status is 'clarificationNeeded' (admin requesting document/clarification), OR
    // 2. Files were previously uploaded and status went back to 'notReceived' (re-upload scenario)
    const showUploadButton = 
        documentReviewStatus === 'clarificationNeeded' || 
        (hadPreviousFiles && documentReviewStatus === 'notReceived');
    
    return { activeFiles, hasDeletedFiles, hadPreviousFiles, showUploadButton };
};

const extractUploadQuestions = (template, intl) => {
    const questions = [];
    template?.pages?.forEach(page => {
        page?.sections?.forEach(section => {
            section?.groups?.forEach(group => {
                group?.questions?.forEach(question => {
                    if (question.type === CONSTANTS.controlTypes.upload) {
                        questions.push({
                            pageId: page.id,
                            questionId: question.id,
                            label: question.labelId 
                                ? intl.formatMessage({ id: question.labelId })
                                : question.label || intl.formatMessage({ id: 'supportingDocumentsUncategorized' }),
                            fileNumberLimit: question.fileNumberLimit || CONSTANTS.supplementalQuestionDetails?.fileDefaultLimit || 5,
                            fileSizeLimit: question.fileSizeLimit || CONSTANTS.supplementalQuestionDetails?.fileSizeLimit,
                            allowedFileTypes: question.allowedFileTypes || CONSTANTS.allowedFileTypes,
                        });
                    }
                });
            });
        });
    });
    return questions;
};

export const useDocumentGroups = (template) => {
    const intl = useIntl();
    const supplementalItems = useSelector(selectSupplementalItems);

    return useMemo(() => {
        const uploadQuestions = extractUploadQuestions(template, intl);
        const processedQuestionIds = new Set();
        
        // Map questions to their files from supplementalItems
        const groups = uploadQuestions.map(({ questionId, label, fileNumberLimit, fileSizeLimit, allowedFileTypes }) => {
            const questionEntry = supplementalItems?.[questionId];
            processedQuestionIds.add(questionId);
            
            if (questionEntry?.files && Array.isArray(questionEntry.files)) {
                const { activeFiles, hasDeletedFiles, showUploadButton } = filterActiveFiles(
                    questionEntry.files,
                    questionEntry.documentReviewStatus
                );
                
                return {
                    label,
                    questionId,
                    files: activeFiles,
                    hasDeletedFiles,
                    showUploadButton,
                    documentReviewStatus: questionEntry.documentReviewStatus,
                    documentReviewUpdatedAt: questionEntry.documentReviewUpdatedAt,
                    documentReviewComments: questionEntry.documentReviewComments,
                    fileNumberLimit,
                    fileSizeLimit,
                    allowedFileTypes
                };
            }
            
            return {
                label,
                questionId,
                files: [],
                hasDeletedFiles: false,
                showUploadButton: false,
                documentReviewStatus: 'notReceived',
                documentReviewUpdatedAt: null,
                documentReviewComments: [],
                fileNumberLimit,
                fileSizeLimit,
                allowedFileTypes
            };
        });
        
        // Handle orphaned files (supplementalItems entries not in template)
        const orphanedEntries = Object.entries(supplementalItems).filter(
            ([questionId]) => !processedQuestionIds.has(questionId)
        );
        
        if (orphanedEntries.length > 0) {
            orphanedEntries.forEach(([questionId, questionEntry]) => {
                if (questionEntry?.files && Array.isArray(questionEntry.files)) {
                    const { activeFiles, hasDeletedFiles, showUploadButton } = filterActiveFiles(
                        questionEntry.files,
                        questionEntry.documentReviewStatus
                    );
                    
                    if (activeFiles.length > 0 || showUploadButton) {
                        groups.push({
                            label: intl.formatMessage({ id: 'supportingDocumentsUncategorized' }),
                            questionId,
                            files: activeFiles,
                            hasDeletedFiles,
                            showUploadButton,
                            documentReviewStatus: questionEntry.documentReviewStatus,
                            documentReviewUpdatedAt: questionEntry.documentReviewUpdatedAt,
                            documentReviewComments: questionEntry.documentReviewComments,
                            fileNumberLimit: CONSTANTS.supplementalQuestionDetails?.fileDefaultLimit || 5,
                            fileSizeLimit: CONSTANTS.supplementalQuestionDetails?.fileSizeLimit,
                            allowedFileTypes: CONSTANTS.allowedFileTypes
                        });
                    }
                }
            });
        }
        
        return groups;
    }, [supplementalItems, template, intl]);
};

export default useDocumentGroups;
